'use client'

import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid'
import Image from 'next/image'

import { getBuilderPreviewMode } from '@src/utils/builder.util'
import { cn } from '@src/utils/tailwind'

import type { SKUSelectorDropdownOption } from './SKUSelectorDropdownProps'

type DropdownProps = {
  dropdownOptions?: SKUSelectorDropdownOption[]
  activeOption?: SKUSelectorDropdownOption
  selectedSku: string
  isLoading: boolean
  onChange: (sku: string) => void
  previewAllItems?: boolean
}

export function Dropdown({
  dropdownOptions,
  activeOption,
  selectedSku,
  isLoading,
  onChange,
  previewAllItems = false,
}: DropdownProps) {
  const isPreview = previewAllItems && getBuilderPreviewMode().edit

  const renderOption = (option: SKUSelectorDropdownOption) => {
    return (
      <div className="flex items-center">
        {option.hasImage && (
          <Image
            src={option.image?.src ?? ''}
            alt={option.image?.altText ?? ''}
            width={option.image?.width ?? 0}
            height={option.image?.height ?? 0}
            style={{
              marginRight: `${option.image?.rightSpacing ?? 0}px`,
            }}
          />
        )}
        {option.label}
        {option?.descriptor && (
          <>
            <span className="mx-2">—</span>
            <span className="text-bodi-green-deep-500">{option.descriptor}</span>
          </>
        )}
      </div>
    )
  }

  if (isPreview) {
    return (
      <div className="flex flex-col gap-4 px-4 pb-4">
        {dropdownOptions?.map((option) => (
          <div
            className="flex flex-col gap-0 rounded-lg border-2 border-dashed border-bodi-blue-info p-4"
            key={option.sku}
          >
            {option.contentTop}
            <div className="my-4 h-px border border-dashed border-bodi-blue-info" />
            {renderOption(option)}
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="flex w-full flex-col">
      {activeOption?.contentTop}
      <Listbox value={selectedSku} onChange={onChange} disabled={isLoading}>
        <ListboxButton className="flex w-full items-center justify-between rounded-lg p-4 text-left outline outline-1 outline-bodi-gray-butters">
          {renderOption(activeOption ?? {})}
          <ChevronUpDownIcon className="size-4" />
        </ListboxButton>
        <ListboxOptions
          anchor="bottom"
          transition
          className={cn(
            'mt-2 w-[var(--button-width)] origin-top rounded-lg bg-bodi-white py-2 outline outline-1 outline-bodi-gray-butters drop-shadow-md',
            'transition duration-200 ease-out data-[closed]:scale-95 data-[closed]:opacity-0'
          )}
        >
          {dropdownOptions?.map((option) => (
            <ListboxOption
              key={option.sku}
              value={option.sku}
              className="flex items-center gap-2 p-3 text-left hover:bg-bodi-green-bright-300"
            >
              <CheckIcon
                className={cn(
                  'size-5 text-transparent',
                  option.sku === selectedSku && 'text-bodi-green-deep-500'
                )}
              />
              {renderOption(option)}
            </ListboxOption>
          ))}
        </ListboxOptions>
      </Listbox>
    </div>
  )
}
